// =============================================================================
// VandieZ admin-web -- Loads list (/loads)
// =============================================================================
// Paginated search across every load. Filters by status + pickup +
// drop district + shipper_id. 50/page. Click a row to open the load
// detail. Pairs with the existing /loads/:loadId detail page.
// =============================================================================

import { AlertCircle, ChevronLeft, ChevronRight, Download, Package, Search } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { loadsAdminApi, streamCsvDownload } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/lib/toast";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminLoadRow } from "@/types/admin_load";

const PAGE = 50;

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Open", value: "open_for_bidding" },
  { label: "Accepted", value: "accepted" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Expired", value: "expired" },
];

function statusClass(s: string): string {
  switch (s) {
    case "open_for_bidding":
      return "bg-info-subtle text-info-subtle-foreground";
    case "accepted":
      return "bg-success-subtle text-success-subtle-foreground";
    case "cancelled":
    case "expired":
      return "bg-danger-subtle text-danger-subtle-foreground";
    case "completed":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-foreground";
  }
}

export function LoadsListPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = searchParams.get("status") ?? "";
  const pickupFilter = searchParams.get("pickup") ?? "";
  const dropFilter = searchParams.get("drop") ?? "";
  const sortParam = searchParams.get("sort") ?? "created_at_desc";
  const createdFrom = searchParams.get("created_from") ?? "";
  const createdTo = searchParams.get("created_to") ?? "";
  const offset = Number(searchParams.get("offset") ?? "0");

  const [rows, setRows] = useState<AdminLoadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickupInput, setPickupInput] = useState(pickupFilter);
  const [dropInput, setDropInput] = useState(dropFilter);

  const fetchRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await loadsAdminApi.list(
          accessToken,
          {
            ...(statusFilter !== "" ? { status: statusFilter } : {}),
            ...(pickupFilter !== "" ? { pickup_district: pickupFilter } : {}),
            ...(dropFilter !== "" ? { drop_district: dropFilter } : {}),
            ...(createdFrom !== "" ? { created_from: createdFrom } : {}),
            ...(createdTo !== "" ? { created_to: createdTo } : {}),
            sort: sortParam,
            limit: PAGE,
            offset,
          },
          signal,
        );
        setRows(res.rows);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      accessToken, statusFilter, pickupFilter, dropFilter,
      createdFrom, createdTo, sortParam, offset,
    ],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRows(c.signal);
    return () => { c.abort(); };
  }, [fetchRows]);

  const updateParam = (key: string, value: string): void => {
    setSearchParams((cur) => {
      const next = new URLSearchParams(cur);
      if (value === "") {next.delete(key);} else {next.set(key, value);}
      // Reset offset when filters change.
      next.delete("offset");
      return next;
    }, { replace: true });
  };

  const [exporting, setExporting] = useState(false);

  /**
   * Streams the current filter as CSV.
   *
   * The export carries the STATUS filter only, matching the other
   * export buttons on this admin — the backend's loads.csv takes
   * `status` and nothing else. Sending the date window would silently
   * do nothing, which is worse than not offering it.
   */
  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const suffix =
        statusFilter !== ""
          ? `?status=${encodeURIComponent(statusFilter)}`
          : "";
      const stamp = new Date().toISOString().slice(0, 10);
      await streamCsvDownload(
        accessToken,
        `/admin/exports/loads.csv${suffix}`,
        `vandiez-loads-${stamp}.csv`,
      );
      toast.success("Export started — check your downloads.");
    } catch (e: unknown) {
      toast.error(
        e instanceof ApiError ? e.problem.detail : "Export failed.",
      );
    } finally {
      setExporting(false);
    }
  };

  const setOffset = (next: number): void => {
    setSearchParams((cur) => {
      const params = new URLSearchParams(cur);
      if (next === 0) {params.delete("offset");}
      else {params.set("offset", String(next));}
      return params;
    });
  };

  const page = Math.floor(offset / PAGE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Loads"
        description={`Paginated search across every load on the platform. ${total} matching filters.`}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void exportCsv(); }}
            disabled={exporting}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" /> Filters
          </CardTitle>
          <CardDescription>
            All filters AND together. District filters use ILIKE so
            partial fragments match.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <div className="mt-1 flex flex-wrap gap-1">
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { updateParam("status", o.value); }}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs",
                    statusFilter === o.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent",
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Sort by
            </label>
            <select
              value={sortParam}
              onChange={(e) => { updateParam("sort", e.target.value); }}
              className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            >
              <option value="created_at_desc">Newest first</option>
              <option value="created_at_asc">Oldest first</option>
              <option value="expiring_soon">Expiring soon</option>
              <option value="budget_max_desc">Highest budget</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Pickup district
            </label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateParam("pickup", pickupInput.trim());
              }}
            >
              <Input
                value={pickupInput}
                onChange={(e) => { setPickupInput(e.target.value); }}
                placeholder="e.g. Chennai"
                className="mt-1"
              />
            </form>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Drop district
            </label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateParam("drop", dropInput.trim());
              }}
            >
              <Input
                value={dropInput}
                onChange={(e) => { setDropInput(e.target.value); }}
                placeholder="e.g. Bangalore"
                className="mt-1"
              />
            </form>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Created from
            </label>
            {/* Native date inputs: the value is already the YYYY-MM-DD
                the API expects, so there is no parsing step to get
                wrong. */}
            <Input
              type="date"
              value={createdFrom}
              max={createdTo !== "" ? createdTo : undefined}
              onChange={(e) => { updateParam("created_from", e.target.value); }}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Created to
            </label>
            {/* The backend treats this as INCLUSIVE of the whole day, so
                picking the same date in both fields returns that day —
                which is what an admin expects and what a naive `<=`
                against a timestamp would not give. */}
            <Input
              type="date"
              value={createdTo}
              min={createdFrom !== "" ? createdFrom : undefined}
              onChange={(e) => { updateParam("created_to", e.target.value); }}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-4 w-4" /> Results
          </CardTitle>
          <CardDescription>
            {rows.length} on this page · page {page} / {totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loading && rows.length === 0 ? (
            <TableSkeleton rows={6} columns={4} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No matching loads.
            </div>
          ) : (
            <ul className="divide-y text-sm">
              {rows.map((r) => (
                <li key={r.load_id}>
                  <Link
                    to={`/loads/${r.load_id}`}
                    className="flex items-center justify-between gap-3 px-2 py-3 hover:bg-muted/40 transition-colors -mx-2 rounded"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs uppercase tracking-wide font-medium",
                            statusClass(r.status),
                          )}
                        >
                          {r.status.replace(/_/g, " ")}
                        </span>
                        <span className="font-medium">
                          {r.pickup_district} → {r.drop_district}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({r.estimated_distance_km} km)
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {r.shipper_name ?? r.shipper_id.slice(0, 8)} ·{" "}
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="font-mono tabular-nums shrink-0 text-right">
                      ₹{Number(r.budget_min).toLocaleString("en-IN")} – ₹
                      {Number(r.budget_max).toLocaleString("en-IN")}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => { setOffset(Math.max(0, offset - PAGE)); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE >= total}
              onClick={() => { setOffset(offset + PAGE); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
